import React, { useEffect, useState } from 'react'
import { apiFetch } from '../../api.js'

export default function SettingsSecurity({ onFlash }) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [sessionTimeout, setSessionTimeout] = useState(5)
  const [maxAttempts, setMaxAttempts] = useState(10)
  const [ipWhitelist, setIpWhitelist] = useState('')
  const [requireEmailVerif, setRequireEmailVerif] = useState(false)

  const [auditRetention, setAuditRetention] = useState(365)
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false)
  const [purgeResult, setPurgeResult] = useState(null)

  useEffect(() => {
    apiFetch('/api/settings/security')
      .then((data) => {
        setSessionTimeout(data.session_timeout_minutes ?? 5)
        setMaxAttempts(data.max_login_attempts ?? 10)
        setIpWhitelist((data.ip_whitelist || []).join('\n'))
        setRequireEmailVerif(!!data.require_email_verification)
      })
      .catch(() => onFlash?.('Impossible de charger la politique de sécurité'))
      .finally(() => setLoading(false))
  }, [])

  async function handleSave(e) {
    e.preventDefault()
    setSaving(true)
    try {
      const ipList = ipWhitelist
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter(Boolean)

      await apiFetch('/api/settings/security', {
        method: 'PATCH',
        body: {
          session_timeout_minutes: Number(sessionTimeout),
          max_login_attempts: Number(maxAttempts),
          ip_whitelist: ipList,
          require_email_verification: requireEmailVerif,
        },
      })
      onFlash?.('Politique de sécurité sauvegardée.')
    } catch (err) {
      onFlash?.('Erreur sauvegarde : ' + (err?.message || 'Erreur'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="p-4 text-slate-500 text-sm">Chargement...</div>
  }

  return (
    <form onSubmit={handleSave} className="space-y-6 max-w-xl">
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Politique de sécurité</h3>
        <p className="text-xs text-slate-500 mb-4">
          Ces paramètres s'appliquent aux utilisateurs de ce compte.
        </p>

        {/* Session timeout */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Délai d'inactivité (verrouillage automatique)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={1440}
              value={sessionTimeout}
              onChange={(e) => setSessionTimeout(e.target.value)}
              className="w-24 rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
            />
            <span className="text-sm text-slate-500">minutes</span>
          </div>
          <p className="text-xs text-slate-400 mt-1">Minimum 1 min, maximum 1440 min (24h). Défaut : 5 min.</p>
        </div>

        {/* Max login attempts */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Tentatives de connexion max avant blocage
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={100}
              value={maxAttempts}
              onChange={(e) => setMaxAttempts(e.target.value)}
              className="w-24 rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
            />
            <span className="text-sm text-slate-500">tentatives</span>
          </div>
          <p className="text-xs text-slate-400 mt-1">Défaut : 10 tentatives.</p>
        </div>

        {/* IP whitelist */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">
            IP autorisées (whitelist)
          </label>
          <textarea
            value={ipWhitelist}
            onChange={(e) => setIpWhitelist(e.target.value)}
            rows={4}
            placeholder={"192.168.1.0/24\n10.0.0.1\n(vide = pas de restriction)"}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
          />
          <p className="text-xs text-slate-400 mt-1">
            Une IP ou CIDR par ligne. Laissez vide pour autoriser toutes les IPs.
          </p>
        </div>

        {/* Require email verification */}
        <div className="mb-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={requireEmailVerif}
              onChange={(e) => setRequireEmailVerif(e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm font-medium text-slate-700">
              Exiger la vérification email avant connexion
            </span>
          </label>
          <p className="text-xs text-slate-400 mt-1 ml-7">
            Si activé, les nouveaux comptes doivent vérifier leur email avant de pouvoir se connecter.
          </p>
        </div>
      </div>

      <div className="border-t border-slate-100 pt-4">
        <button
          type="submit"
          disabled={saving}
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
        >
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </button>
      </div>

      {/* Rétention des logs d'audit */}
      <div className="rounded-3xl border border-slate-200 bg-white p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-1">Rétention des logs d'audit</h3>
        <p className="text-xs text-slate-500 mb-4">Définissez la durée de conservation des logs. La purge supprime définitivement les entrées plus anciennes.</p>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <select
            value={auditRetention}
            onChange={e => setAuditRetention(Number(e.target.value))}
            className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
          >
            <option value={90}>90 jours</option>
            <option value={180}>6 mois</option>
            <option value={365}>1 an (recommandé)</option>
            <option value={730}>2 ans</option>
          </select>
          <button
            type="button"
            onClick={() => setShowPurgeConfirm(true)}
            className="rounded-2xl bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700"
          >
            Purger maintenant
          </button>
        </div>
        {purgeResult && (
          <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2 text-sm text-emerald-700">
            {purgeResult.deleted_count} log(s) supprimé(s) (rétention : {purgeResult.retention_days}j)
          </div>
        )}
      </div>

      {/* Purge confirm modal */}
      {showPurgeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Confirmer la purge</h3>
            <p className="text-sm text-slate-600 mb-4">
              Vous êtes sur le point de supprimer définitivement tous les logs d'audit
              antérieurs à <strong>{auditRetention} jours</strong>. Cette action est irréversible.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowPurgeConfirm(false)}
                className="rounded-2xl border border-slate-200 px-4 py-2 text-sm">Annuler</button>
              <button
                type="button"
                onClick={async () => {
                  setShowPurgeConfirm(false)
                  try {
                    const r = await apiFetch('/api/audit/purge', { method: 'POST', body: { retention_days: auditRetention } })
                    setPurgeResult(r)
                  } catch (e) { alert(String(e?.message || e)) }
                }}
                className="rounded-2xl bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700">
                Confirmer la purge
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  )
}
